import { supabase } from '../lib/supabaseClient';
import { completarTrabajadores } from './trabajadoresDeMisTiendas';
import type { EstadoAsignacionVigente } from '../domain/replacement';
import type { CampaignRow, PdvRow } from '../lib/db-types';

export interface WorkerDayAssignment {
  id: string;
  status: string;
  positions: {
    id: string;
    date: string;
    start_time: string;
    end_time: string;
    pdv: { code: string; name: string };
  };
}

export interface ProposalListItem {
  id: string;
  status: string;
  created_at: string;
  /** Viaja para que `completarTrabajadores` rellene `worker` cuando el embed llega en null. */
  worker_id?: string | null;
  worker: { id: string; dni: string; first_names: string; last_names: string };
  positions: {
    id: string;
    date: string;
    start_time: string;
    end_time: string;
    pdv: { code: string; name: string };
  };
}

/**
 * Crea una asignación en estado `propuesta` para la posición dada.
 *
 * Va por la RPC `assign_position` (security definer, coordinador|admin) y NO por un insert
 * directo: el bloqueo duro de cruce de horarios tiene que evaluarse en el servidor, porque
 * un coordinador NO ve por RLS las asignaciones del mismo recurso en campañas de otro
 * coordinador (la comprobación client-side de `AsignarModal` es solo feedback anticipado).
 * La RPC también fija `assigned_by = auth.uid()` y escribe `audit_log`.
 */
export async function createAssignment(positionId: string, workerId: string): Promise<{ id: string }> {
  const { data, error } = await supabase.rpc('assign_position', {
    p_position_id: positionId,
    p_worker_id: workerId,
  });
  if (error) {
    if (error.code === '23505') throw new Error('La posición ya tiene una asignación activa.');
    // La RPC levanta excepciones con mensaje en español ya listo para mostrar
    // ("cruce de horario", "posición ocupada", "no autorizado").
    throw new Error(error.message);
  }
  if (!data) throw new Error('No se pudo crear la propuesta. Intenta de nuevo.');
  return { id: data as string };
}

/** Asignaciones activas (propuesta|aprobada) de un trabajador en una fecha (YYYY-MM-DD). */
export async function listWorkerAssignments(workerId: string, date: string): Promise<WorkerDayAssignment[]> {
  const { data, error } = await supabase
    .from('assignments')
    .select('id, status, positions!inner(id, date, start_time, end_time, pdv(code, name))')
    .eq('worker_id', workerId)
    .in('status', ['propuesta', 'aprobada'])
    .eq('positions.date', date);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as WorkerDayAssignment[];
}

/** Asignaciones de una campaña filtradas por estado. */
export async function listAssignments(
  campaignId: string,
  status: 'propuesta' | 'aprobada' | 'rechazada' | 'reemplazada'
): Promise<ProposalListItem[]> {
  const { data, error } = await supabase
    .from('assignments')
    .select(
      'id, status, created_at, worker_id, ' +
      'worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names), ' +
      'positions!inner(id, date, start_time, end_time, pdv(code, name))'
    )
    .eq('positions.campaign_id', campaignId)
    .eq('status', status)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as ProposalListItem[];
  // El supervisor no lee `profiles`: para él el embed llega en null y se completa por la RPC.
  await completarTrabajadores(rows);
  return rows;
}

/**
 * Retira una propuesta borrándola (solo si sigue en estado `propuesta`).
 *
 * Vía elegida: DELETE. Depende de la política `assignments_delete_propuesta` del Grupo A
 * (`0002_rls.sql`), que permite el borrado a admin y al coordinador de la campaña cuando
 * `status = 'propuesta'`. Como el DELETE puede afectar 0 filas tanto porque la propuesta ya
 * fue procesada como porque el usuario no coordina esa campaña, el mensaje nombra ambas
 * causas en vez de afirmar solo una.
 */
export async function removeProposal(id: string): Promise<void> {
  const { data, error } = await supabase
    .from('assignments')
    .delete()
    .eq('id', id)
    .eq('status', 'propuesta')
    .select('id');
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) {
    throw new Error(
      'No se pudo retirar: la propuesta ya fue aprobada o rechazada, o no coordinas esa campaña.'
    );
  }
}

// ---------------------------------------------------------------------------
// Cambiar el recurso de una posición YA asignada
//
// El índice único parcial `assignments_una_viva_por_posicion_idx` impide que una posición
// tenga dos asignaciones vivas, así que "cambiar de persona" no es un UPDATE del worker_id
// (perdería el rastro de quién estaba antes) ni un insert nuevo (chocaría con el índice):
// es mover la vigente a `reemplazada` y crear la nueva en `propuesta`, en UNA transacción.
// Eso lo hace la RPC `replace_assignment` (0003, reforzada en 0018).
// ---------------------------------------------------------------------------

/** Una posición con asignación viva, aplanada para la pestaña "Asignadas". */
export interface AssignedPositionRow {
  assignmentId: string;
  assignmentStatus: EstadoAsignacionVigente;
  positionId: string;
  date: string;
  startTime: string;
  endTime: string;
  slotIndex: number;
  /** `positions.status = 'cerrada'`: periodo liquidado, ya no se toca. */
  posicionCerrada: boolean;
  /** Marcas de asistencia de la asignación vigente: con marcas NO se cambia la persona. */
  marcas: number;
  pdv: { id: string; code: string; name: string } | null;
  worker: { id: string; dni: string; firstNames: string; lastNames: string } | null;
}

export interface AssignedPositionFilter {
  dateFrom?: string;
  dateTo?: string;
  pdvId?: string;
}

/** Igual que en `api/positions.ts`: PostgREST trunca en silencio a 1000 filas. */
const ASSIGNED_PAGE_SIZE = 1000;

const ASSIGNED_SELECT =
  `id, date, start_time, end_time, slot_index, status,
   pdv:pdv(id, code, name),
   assignments!inner(id, status, worker_id,
     worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names),
     attendance(id))`;

/**
 * Posiciones de la campaña que YA tienen recurso (asignación `propuesta` o `aprobada`),
 * con quién es, su DNI, la fecha, la tienda, el horario y el estado de la asignación.
 *
 * Se consulta desde `positions` y no desde `assignments` para poder filtrar por fecha y por
 * PDV —que viven en la posición— sin depender de un embed anidado, y para traer de una vez
 * `positions.status` (una posición `cerrada` no se puede tocar) y el conteo de `attendance`
 * (con marcado tampoco). Ambos datos son los que apagan el botón "Cambiar recurso"; la
 * palabra final la tiene igual el servidor.
 */
export async function listAssignedPositions(
  campaignId: string,
  filtros: AssignedPositionFilter = {},
): Promise<AssignedPositionRow[]> {
  type Raw = {
    id: string; date: string; start_time: string; end_time: string;
    slot_index: number; status: string;
    pdv: { id: string; code: string; name: string } | null;
    assignments: {
      id: string; status: string;
      worker_id: string | null;
      worker: { id: string; dni: string; first_names: string; last_names: string } | null;
      attendance: { id: string }[] | null;
    }[];
  };
  const raw: Raw[] = [];
  let total: number | null = null;
  for (let from = 0; ; from += ASSIGNED_PAGE_SIZE) {
    let q = supabase
      .from('positions')
      .select(ASSIGNED_SELECT, { count: 'exact' })
      .eq('campaign_id', campaignId)
      .in('assignments.status', ['propuesta', 'aprobada']);
    if (filtros.dateFrom) q = q.gte('date', filtros.dateFrom);
    if (filtros.dateTo) q = q.lte('date', filtros.dateTo);
    if (filtros.pdvId) q = q.eq('pdv_id', filtros.pdvId);
    const { data, error, count } = await q
      .order('date')
      .order('start_time')
      .order('id')
      .range(from, from + ASSIGNED_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    if (count !== null && count !== undefined) total = count;
    const batch = (data ?? []) as unknown as Raw[];
    raw.push(...batch);
    if (batch.length < ASSIGNED_PAGE_SIZE) break;
  }
  if (total !== null && raw.length !== total) {
    throw new Error(
      `Lectura incompleta de posiciones asignadas: se recibieron ${raw.length} de ${total} filas. ` +
      'Acota el rango de fechas y vuelve a intentar.',
    );
  }

  // El supervisor no lee `profiles`: el embed anidado llega en null y se completa por la RPC.
  await completarTrabajadores(raw.flatMap((p) => p.assignments ?? []));

  return raw.flatMap((p) =>
    (p.assignments ?? []).map((a) => ({
      assignmentId: a.id,
      assignmentStatus: a.status as EstadoAsignacionVigente,
      positionId: p.id,
      date: p.date,
      startTime: p.start_time,
      endTime: p.end_time,
      slotIndex: p.slot_index,
      posicionCerrada: p.status === 'cerrada',
      marcas: (a.attendance ?? []).length,
      pdv: p.pdv,
      worker: a.worker
        ? {
            id: a.worker.id,
            dni: a.worker.dni,
            firstNames: a.worker.first_names,
            lastNames: a.worker.last_names,
          }
        : null,
    })),
  );
}

/**
 * Cambia el recurso de una posición ya asignada, vía RPC `replace_assignment`.
 *
 * En una transacción: marca la asignación vigente como `reemplazada`, crea la nueva en
 * `propuesta` sobre la MISMA posición, valida el cruce de horario del que entra y audita.
 * La 0018 le agregó la guarda que aquí importa: **si la posición ya tiene asistencia
 * marcada, rechaza el cambio**. Esa validación vive en el servidor porque la RPC es
 * `security definer` y cualquiera con rol coordinador podría llamarla sin pasar por la
 * pantalla; lo que la UI hace con `marcas` es solo adelantar el aviso.
 *
 * Los mensajes de la RPC ya vienen en español y nombran la causa (cruce de horario, DNI no
 * registrado, asistencia marcada, posición cerrada): se muestran tal cual.
 */
export async function replaceAssignment(
  assignmentId: string,
  nuevoDni: string,
): Promise<{ id: string }> {
  const { data, error } = await supabase.rpc('replace_assignment', {
    p_assignment: assignmentId,
    p_new_worker_dni: nuevoDni,
  });
  if (error) {
    if (error.code === '23505') {
      throw new Error(
        'La posición ya recibió otra asignación mientras confirmabas. Actualiza la lista y revisa quién quedó.',
      );
    }
    throw new Error(error.message);
  }
  if (!data) throw new Error('No se pudo cambiar el recurso. Intenta de nuevo.');
  return { id: data as string };
}

export interface BulkAssignInput {
  position_id: string;
  worker_dni: string;
}

export interface BulkAssignResult {
  position_id: string;
  worker_dni: string;
  ok: boolean;
  error: string | null;
}

/** Carga masiva vía RPC `bulk_assign`: crea propuestas y devuelve el resultado por fila. */
export async function bulkAssign(rows: BulkAssignInput[]): Promise<BulkAssignResult[]> {
  const { data, error } = await supabase.rpc('bulk_assign', { rows });
  if (error) throw new Error(error.message);
  return data as BulkAssignResult[];
}

export interface PendingProposal {
  id: string;
  created_at: string;
  /**
   * true si la posición ya tuvo una asignación en estado `reemplazada`, es decir, esta
   * propuesta viene del flujo de reemplazo (Task 38). La bandeja la marca con el badge
   * "Reemplazo" para la aprobación rápida que pide la spec §7.
   */
  is_replacement: boolean;
  /** Viaja para que `completarTrabajadores` rellene `worker` cuando el embed llega en null. */
  worker_id?: string | null;
  worker: { id: string; dni: string; first_names: string; last_names: string };
  positions: {
    id: string;
    date: string;
    start_time: string;
    end_time: string;
    campaign_id: string;
    campaigns: { client: string; brand: string };
    pdv: { code: string; name: string };
  };
}

/**
 * Todas las propuestas pendientes de aprobación (bandeja del jefe).
 *
 * Marca `is_replacement` en las propuestas cuya posición ya tuvo una asignación
 * `reemplazada` (flujo de reemplazo de la Task 38): esas son las que la spec §7 quiere
 * ver destacadas para la "aprobación rápida" del jefe.
 */
export async function listAllProposals(): Promise<PendingProposal[]> {
  const { data, error } = await supabase
    .from('assignments')
    .select(
      'id, created_at, worker_id, ' +
      'worker:profiles!assignments_worker_id_fkey(id, dni, first_names, last_names), ' +
      'positions!inner(id, date, start_time, end_time, campaign_id, campaigns(client, brand), pdv(code, name))'
    )
    .eq('status', 'propuesta')
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as Omit<PendingProposal, 'is_replacement'>[];
  if (rows.length === 0) return [];
  // El supervisor no lee `profiles`: para él el embed llega en null y se completa por la RPC.
  await completarTrabajadores(rows);

  const positionIds = [...new Set(rows.map((r) => r.positions.id))];
  const { data: replaced, error: replacedError } = await supabase
    .from('assignments')
    .select('position_id')
    .eq('status', 'reemplazada')
    .in('position_id', positionIds);
  if (replacedError) throw new Error(replacedError.message);
  const replacedPositions = new Set((replaced ?? []).map((r) => r.position_id as string));

  return rows.map((r) => ({ ...r, is_replacement: replacedPositions.has(r.positions.id) }));
}

/**
 * Aprueba (true) o rechaza (false) propuestas vía RPC `approve_assignments`.
 * La RPC actualiza positions y escribe audit_log. Retorna filas afectadas.
 */
export async function approveAssignments(ids: string[], approve: boolean): Promise<number> {
  const { data, error } = await supabase.rpc('approve_assignments', { ids, approve });
  if (error) throw new Error(error.message);
  return data as number;
}

/**
 * Conteos para el badge de la bandeja. La spec §7 define la bandeja del jefe como
 * "trabajadores nuevos, asignaciones y cierres pendientes": los tres entran en el badge.
 */
export async function countPendingApprovals(): Promise<{ proposals: number; applications: number; closures: number }> {
  const [a, b, c] = await Promise.all([
    supabase.from('assignments').select('id', { count: 'exact', head: true }).eq('status', 'propuesta'),
    supabase.from('worker_applications').select('id', { count: 'exact', head: true }).eq('status', 'pendiente'),
    supabase.from('payroll_closures').select('id', { count: 'exact', head: true }).eq('status', 'borrador'),
  ]);
  if (a.error) throw new Error(a.error.message);
  if (b.error) throw new Error(b.error.message);
  if (c.error) throw new Error(c.error.message);
  return { proposals: a.count ?? 0, applications: b.count ?? 0, closures: c.count ?? 0 };
}

export interface MyShiftRow {
  id: string;
  status: string;
  position: {
    id: string;
    campaign_id: string;
    date: string;
    start_time: string;
    end_time: string;
    /** Tarifa de ESA jornada, para el resumen del mes del recurso. */
    rate_amount: number;
    rate_unit: 'jornada' | 'hora';
    pdv: PdvRow;
    campaign: CampaignRow;
  };
}

const MY_SHIFT_SELECT = `id, status,
  position:positions!inner(id, campaign_id, date, start_time, end_time, rate_amount, rate_unit,
    pdv:pdv(*), campaign:campaigns(*))`;

/** Turnos APROBADOS del usuario logueado para esa fecha (YYYY-MM-DD en America/Lima),
 *  ordenados por hora de inicio. Puede haber más de uno el mismo día (turnos que no se
 *  cruzan, en PDV distintos): la app móvil muestra una tarjeta por cada uno.
 *  PostgREST no ordena el padre por una columna embebida, así que se ordena en cliente. */
/**
 * Mis turnos aprobados en un RANGO, para el panel del recurso.
 *
 * El celular de campo responde "¿qué hago hoy?"; esto responde "¿qué tengo este mes?", que es
 * lo que la persona necesita para organizarse y para saber cuánto va a cobrar. Filtra por
 * `worker_id` de forma explícita: es "mío", no "lo que la RLS deje pasar".
 */
export async function getMyShiftsRange(desde: string, hasta: string): Promise<MyShiftRow[]> {
  const { data: userData } = await supabase.auth.getUser();
  const yo = userData?.user?.id;
  if (!yo) return [];
  const { data, error } = await supabase
    .from('assignments')
    .select(MY_SHIFT_SELECT)
    .eq('worker_id', yo)
    .eq('status', 'aprobada')
    .gte('position.date', desde)
    .lte('position.date', hasta);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as MyShiftRow[];
  rows.sort((a, b) =>
    a.position.date === b.position.date
      ? a.position.start_time.localeCompare(b.position.start_time)
      : a.position.date.localeCompare(b.position.date));
  return rows;
}

export async function getMyShifts(dateISO: string): Promise<MyShiftRow[]> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error('Sesión no válida. Vuelve a iniciar sesión.');
  const { data, error } = await supabase
    .from('assignments')
    .select(MY_SHIFT_SELECT)
    .eq('worker_id', userData.user.id)
    .eq('status', 'aprobada')
    .eq('position.date', dateISO);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as MyShiftRow[];
  rows.sort((a, b) => a.position.start_time.localeCompare(b.position.start_time));
  return rows;
}

/** Un turno aprobado PROPIO por id de asignación. Lo usa `/movil/marcar?assignment=<id>`
 *  para saber exactamente cuál de los turnos del día se está marcando. Devuelve null si
 *  el id no existe, no es del usuario o la asignación ya no está aprobada. */
export async function getMyShiftById(assignmentId: string): Promise<MyShiftRow | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error('Sesión no válida. Vuelve a iniciar sesión.');
  const { data, error } = await supabase
    .from('assignments')
    .select(MY_SHIFT_SELECT)
    .eq('id', assignmentId)
    .eq('worker_id', userData.user.id)
    .eq('status', 'aprobada')
    .limit(1);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as MyShiftRow[];
  return rows[0] ?? null;
}

/** Próximo turno aprobado después de `afterDateISO`. PostgREST no ordena el padre por
 *  una columna embebida, así que traemos los siguientes 20 y ordenamos en el cliente. */
/**
 * Se crea (o devuelve) un turno de ENSAYO de hoy en el banco de pruebas, para el propio usuario.
 *
 * Existe porque el equipo de oficina no tiene turnos y, sin uno, no puede probar la pantalla de
 * marcar: ve "no tienes turno programado" y ahí se acaba. Pedido del usuario el 2026-08-15:
 * *"quisiera tener habilitado ese tipo de herramienta para hacer prueba"*.
 *
 * Se genera bajo demanda y no se siembra por adelantado: un turno sembrado sirve el día que se
 * sembró y después estorba. El servidor lo hace idempotente por día, así que tocar el botón dos
 * veces no deja dos turnos.
 */
export async function crearTurnoDePrueba(): Promise<string> {
  const { data, error } = await supabase.rpc('crear_turno_de_prueba');
  if (error) throw new Error(error.message);
  return data as string;
}

export async function getNextShift(afterDateISO: string): Promise<MyShiftRow | null> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) throw new Error('Sesión no válida. Vuelve a iniciar sesión.');
  const { data, error } = await supabase
    .from('assignments')
    .select(MY_SHIFT_SELECT)
    .eq('worker_id', userData.user.id)
    .eq('status', 'aprobada')
    .gt('position.date', afterDateISO)
    .limit(20);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as MyShiftRow[];
  rows.sort(
    (a, b) =>
      a.position.date.localeCompare(b.position.date) ||
      a.position.start_time.localeCompare(b.position.start_time),
  );
  return rows[0] ?? null;
}

/**
 * Libera un turno asignado que TODAVIA NO SE EJECUTO (0102).
 *
 * No inventa un estado: pasa la asignacion a `rechazada`, y como el indice que reserva la posicion
 * es PARCIAL sobre `propuesta`/`aprobada`, eso la libera sola. La posicion vuelve a `abierta`.
 *
 * ⚠️ La RPC la NIEGA si hay evidencia colgando —marcas de asistencia, canjes, cierres de turno o
 * visitas de levantamiento— y tambien si la posicion pertenece a un periodo ya liquidado. El
 * mensaje del servidor dice CUANTAS hay de cada tipo y que hacer en su lugar, asi que se muestra
 * tal cual y no se traduce: es la parte util.
 */
export async function desasignarTurno(assignmentId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('desasignar_turno', {
    p_assignment: assignmentId,
    p_motivo: motivo,
  });
  if (error) throw new Error(error.message);
}
